import { useState, useId } from 'react';
import type { ReactNode, ElementType } from 'react';
import Collapse from '@mui/material/Collapse';
import ExpandMore from '@mui/icons-material/ExpandMore';

import Divider from '@classify/react-core-components/Divider';
import { styled } from '@classify/react-core-components/styles';
import Typography from '@classify/react-core-components/Typography';
import Box from '@classify/react-core-components/Box';

interface Props {
  children?: ReactNode;
  title: string;
  isCollapsable?: boolean;
  component?: ElementType;
}

const Container = styled(Box)(({ theme: { spacing, breakpoints } }) => ({
  paddingLeft: 0,
  margin: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(2),
  [breakpoints.up('sm')]: {
    gap: spacing(1),
  },
}));

const TogglerButton = styled('button')(({ theme: { spacing, transitions } }) => ({
  width: '100%',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  height: 48,
  margin: spacing(1, 0),
  padding: 0,
  position: 'relative',

  '& > svg': {
    position: 'absolute',
    right: spacing(1),
    top: 11,
    transition: transitions.create('transform', {
      duration: transitions.duration.shortest,
    }),
  },

  '&[aria-expanded=true] > svg': {
    transform: 'rotate(-180deg)',
  },
}));

const Title = styled(Typography)(({ theme: { spacing, breakpoints } }) => ({
  margin: spacing(2, 0),
  [breakpoints.up('sm')]: {
    margin: spacing(0, 0, 2, 0),
  },
})) as typeof Typography;

function NonCollapsableWrapper({ title, children }: Pick<Props, 'title' | 'children'>) {
  return (
    <>
      <Title component="div" variant="overline">
        {title}
      </Title>

      {children}
    </>
  );
}

function CollapsableWrapper({ title, children }: Pick<Props, 'title' | 'children'>) {
  const [isOpen, setIsOpen] = useState(false);
  const uuid = useId();

  const handleClick = () => setIsOpen(!isOpen);

  return (
    <>
      <Title
        component={TogglerButton}
        variant="overline"
        onClick={handleClick}
        aria-expanded={isOpen}
        aria-controls={uuid}
        sx={{ margin: 0 }}
      >
        {title}
        <ExpandMore aria-hidden="true" />
      </Title>
      <Collapse in={isOpen} timeout="auto" id={uuid}>
        {children}
      </Collapse>
      <Divider />
    </>
  );
}

export default function FooterItemsContainer({
  children,
  title,
  isCollapsable,
  component = 'ul',
}: Props) {
  const Wrapper = isCollapsable ? CollapsableWrapper : NonCollapsableWrapper;

  return (
    <div>
      <Wrapper title={title}>
        <Container component={component}>{children}</Container>
      </Wrapper>
    </div>
  );
}
